'use client';

import { c as _c } from "react/compiler-runtime";
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectIsCompactViewport } from '@repo/store/maps/slices/clientContext';
import { clearSelectedHeatmapEvent, selectExternalMapId, selectSelectedHeatmapEvent, selectTitleId, selectTitleSlug } from '@repo/store/maps/slices/mapData';
import type { MapMouseEvent } from 'maplibre-gl';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveHeatmapTitleConfig } from '../game-map/data/heatmap-config';
import { getHeatmapEventOption, heatmapVideoUrl } from '../game-map/data/heatmap-events';
import { getHeatmapEventCategoryLabelSelector } from '../game-map/data/heatmap-i18n';
import { MARKER_LAYER_PREFIX } from '../game-map/hooks/useHeatmapLayer/constants';
import { useMapControls } from '../game-map/hooks/useMapControls';
import { useMapContext } from '../game-map/map-context';
import { LocationContent } from '../location-info/location-content';
import { PinMedia } from './pin-media/pin-media';
const MAX_HEIGHT = 'min(500px, calc(100vh - 56px - 16px))';
const CONTENT_PADDING = '20';
const CONTENT_PADDING_MOBILE = '16';
const CONTENT_PADDING_COMPACT = '12';
const headerIconStyles = css.raw({
  boxShadow: '[0px 1px 0px 0px rgba(0,0,0,0.3)]'
});
const categoryIconWithMediaStyles = css.raw({
  width: '[48px]',
  height: '[48px]',
  minWidth: '[48px]',
  minHeight: '[48px]',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'sm',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '[#0a0a0a]'
});
const clipNavButtonStyles = css.raw({
  position: 'absolute',
  top: '[50%]',
  transform: 'translateY(-50%)',
  zIndex: '[3]',
  boxShadow: '[0px 1px 0px 0px rgba(0,0,0,0.3)]'
});

/**
 * Location-info-style pane for heatmap hotspot markers.
 * Cycles through the cluster's `videos.sample` clips with prev/next controls.
 */
export const HeatmapEventInfo: React.FC<{
  isMobilePane?: boolean;
}> = t0 => {
  const $ = _c(166);
  const {
    isMobilePane: t1
  } = t0;
  const isMobilePane = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const selected = useAppSelector(selectSelectedHeatmapEvent);
  const isCompactViewport = useAppSelector(selectIsCompactViewport);
  const titleSlug = useAppSelector(selectTitleSlug);
  const titleId = useAppSelector(selectTitleId);
  const mapExternalId = useAppSelector(selectExternalMapId);
  const {
    map
  } = useMapContext();
  const {
    flyTo
  } = useMapControls();
  const [modalOpen, setModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [clipIndex, setClipIndex] = useState(0);
  const [clipHotspotId, setClipHotspotId] = useState(null);
  let t3;
  if ($[1] !== mapExternalId || $[2] !== titleId || $[3] !== titleSlug) {
    t3 = resolveHeatmapTitleConfig({
      titleSlug,
      titleId,
      mapExternalId
    });
    $[1] = mapExternalId;
    $[2] = titleId;
    $[3] = titleSlug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const titleConfig = t3;
  let t4;
  if ($[5] !== selected?.videoIds) {
    t4 = selected?.videoIds ?? [];
    $[5] = selected?.videoIds;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const videoIds = t4;
  const clipCount = videoIds.length;
  if (selected?.id !== clipHotspotId) {
    setClipHotspotId(selected?.id ?? null);
    setClipIndex(0);
  }
  const activeClipIndex = clipCount > 0 ? Math.min(clipIndex, clipCount - 1) : 0;
  const activeVideoId = videoIds[activeClipIndex];
  let t5;
  if ($[7] !== activeVideoId) {
    t5 = activeVideoId ? heatmapVideoUrl(activeVideoId) : undefined;
    $[7] = activeVideoId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const videoPath = t5;
  const showClipNav = clipCount > 1;
  let t6;
  if ($[9] !== dispatch) {
    t6 = () => {
      dispatch(clearSelectedHeatmapEvent());
    };
    $[9] = dispatch;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleClose = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setClipIndex(_temp);
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const goToPreviousClip = t7;
  let t8;
  if ($[12] !== clipCount) {
    t8 = () => {
      setClipIndex(index_0 => index_0 < clipCount - 1 ? index_0 + 1 : index_0);
    };
    $[12] = clipCount;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const goToNextClip = t8;
  let t10;
  let t9;
  if ($[14] !== goToNextClip || $[15] !== lightboxOpen || $[16] !== modalOpen || $[17] !== selected || $[18] !== showClipNav) {
    t9 = () => {
      if (!selected || modalOpen || lightboxOpen || !showClipNav) {
        return;
      }
      const onKeyDown = event => {
        const target = event.target;
        if (target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) {
          return;
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          goToPreviousClip();
        } else {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            goToNextClip();
          }
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t10 = [goToNextClip, goToPreviousClip, lightboxOpen, modalOpen, selected, showClipNav];
    $[14] = goToNextClip;
    $[15] = lightboxOpen;
    $[16] = modalOpen;
    $[17] = selected;
    $[18] = showClipNav;
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[21] !== handleClose || $[22] !== lightboxOpen || $[23] !== map || $[24] !== modalOpen || $[25] !== selected) {
    t11 = () => {
      if (!map || !selected || modalOpen || lightboxOpen) {
        return;
      }
      const onMapClick = e => {
        const markerLayers = map.getStyle()?.layers?.map(_temp2).filter(_temp3) ?? [];
        if (markerLayers.length > 0) {
          const hits = map.queryRenderedFeatures(e.point, {
            layers: markerLayers
          });
          if (hits.length > 0) {
            return;
          }
        }
        handleClose();
      };
      map.on("click", onMapClick);
      return () => {
        map.off("click", onMapClick);
      };
    };
    t12 = [handleClose, lightboxOpen, map, modalOpen, selected];
    $[21] = handleClose;
    $[22] = lightboxOpen;
    $[23] = map;
    $[24] = modalOpen;
    $[25] = selected;
    $[26] = t11;
    $[27] = t12;
  } else {
    t11 = $[26];
    t12 = $[27];
  }
  useEffect(t11, t12);
  if (!selected) {
    return null;
  }
  let t13;
  if ($[28] !== selected.eventType || $[29] !== titleConfig) {
    t13 = getHeatmapEventOption(titleConfig, selected.eventType);
    $[28] = selected.eventType;
    $[29] = titleConfig;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const option = t13;
  if (!option) {
    return null;
  }
  let t14;
  if ($[31] !== selected.eventType || $[32] !== t) {
    const categorySelector = getHeatmapEventCategoryLabelSelector(selected.eventType, "singular");
    const gameCategoryName = categorySelector ? t(categorySelector) : selected.eventType;
    t14 = t(_temp4, {
      gameCategoryName
    });
    $[31] = selected.eventType;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const title = t14;
  let t15;
  if ($[34] !== t) {
    t15 = t(_temp5);
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const description = t15;
  const iconColor = option.iconColor ?? "#FFFFFF";
  const padding = isMobilePane ? CONTENT_PADDING_MOBILE : isCompactViewport ? CONTENT_PADDING_COMPACT : CONTENT_PADDING;
  let t16;
  if ($[36] !== flyTo || $[37] !== selected.coordinates) {
    t16 = () => {
      flyTo({
        center: selected.coordinates,
        zoom: Math.max(12, 14),
        duration: 1500
      });
    };
    $[36] = flyTo;
    $[37] = selected.coordinates;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const handlePanToLocation = t16;
  const t17 = isMobilePane ? "12" : "8";
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = css.raw({
      pointerEvents: "auto"
    });
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setModalOpen(true);
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t) {
    t20 = t(_temp6);
    $[41] = t;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = css(headerIconStyles);
    t22 = <Icon icon="open_in_full" decorative={true} />;
    $[43] = t21;
    $[44] = t22;
  } else {
    t21 = $[43];
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t20) {
    t23 = <IconButton onClick={t19} color="white" size="sm" aria-label={t20} className={t21}>{t22}</IconButton>;
    $[45] = t20;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t) {
    t24 = t(_temp7);
    $[47] = t;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = css(headerIconStyles);
    t26 = <Icon icon="close" decorative={true} />;
    $[49] = t25;
    $[50] = t26;
  } else {
    t25 = $[49];
    t26 = $[50];
  }
  let t27;
  if ($[51] !== handleClose || $[52] !== t24) {
    t27 = <IconButton onClick={handleClose} color="white" size="sm" aria-label={t24} className={t25}>{t26}</IconButton>;
    $[51] = handleClose;
    $[52] = t24;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t17 || $[55] !== t18 || $[56] !== t23 || $[57] !== t27) {
    t28 = <FlexRow align="center" gap={t17} className={t18}>{t23}{t27}</FlexRow>;
    $[54] = t17;
    $[55] = t18;
    $[56] = t23;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  const headerActions = t28;
  let t29;
  if ($[59] !== activeClipIndex || $[60] !== clipCount || $[61] !== goToNextClip || $[62] !== showClipNav || $[63] !== t) {
    t29 = showClipNav ? <><IconButton onClick={event_0 => {
        event_0.stopPropagation();
        goToPreviousClip();
      }} color="white" size="sm" disabled={activeClipIndex === 0} aria-label={t(_temp8)} className={css({
        ...clipNavButtonStyles,
        left: "8"
      })}><Icon icon="chevron_left" decorative={true} /></IconButton><IconButton onClick={event_1 => {
        event_1.stopPropagation();
        goToNextClip();
      }} color="white" size="sm" disabled={activeClipIndex >= clipCount - 1} aria-label={t(_temp9)} className={css({
        ...clipNavButtonStyles,
        right: "8"
      })}><Icon icon="chevron_right" decorative={true} /></IconButton><div className={css({
        position: "absolute",
        bottom: "8",
        left: "[50%]",
        transform: "translateX(-50%)",
        zIndex: "[3]",
        pointerEvents: "none",
        backgroundColor: "[rgba(0,0,0,0.55)]",
        borderRadius: "full",
        paddingX: "8",
        paddingY: "2"
      })}><Type variant="caption" color="white">{activeClipIndex + 1}/{clipCount}</Type></div></> : null;
    $[59] = activeClipIndex;
    $[60] = clipCount;
    $[61] = goToNextClip;
    $[62] = showClipNav;
    $[63] = t;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  const clipNav = t29;
  let t30;
  if ($[65] !== iconColor || $[66] !== option.color) {
    t30 = {
      backgroundColor: option.color,
      color: iconColor
    };
    $[65] = iconColor;
    $[66] = option.color;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = css(categoryIconWithMediaStyles);
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== option.icon) {
    t32 = <Icon icon={option.icon} fill={true} decorative={true} />;
    $[69] = option.icon;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== handlePanToLocation || $[72] !== t30 || $[73] !== t32 || $[74] !== title) {
    t33 = <IconButton variant="ghost" style={t30} onClick={handlePanToLocation} color="primary" size="lg" aria-label={title} className={t31}>{t32}</IconButton>;
    $[71] = handlePanToLocation;
    $[72] = t30;
    $[73] = t32;
    $[74] = title;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  const categoryIcon = t33;
  let t34;
  if ($[76] !== headerActions || $[77] !== isMobilePane || $[78] !== padding) {
    t34 = !isMobilePane && <div className={css({
      position: "sticky",
      top: "none",
      zIndex: "[4]",
      height: "[0]",
      pointerEvents: "none"
    })}><div className={css({
        position: "absolute",
        top: "none",
        right: "none",
        padding,
        pointerEvents: "auto"
      })}>{headerActions}</div></div>;
    $[76] = headerActions;
    $[77] = isMobilePane;
    $[78] = padding;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = css({
      position: "relative"
    });
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  if ($[81] !== isCompactViewport || $[82] !== isMobilePane || $[83] !== lightboxOpen || $[84] !== title || $[85] !== videoPath) {
    t36 = videoPath ? <PinMedia videoPath={videoPath} title={title} hero={true} tallHero={true} compact={isCompactViewport && !isMobilePane} lightboxOpen={lightboxOpen} onLightboxChange={setLightboxOpen} /> : null;
    $[81] = isCompactViewport;
    $[82] = isMobilePane;
    $[83] = lightboxOpen;
    $[84] = title;
    $[85] = videoPath;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== headerActions || $[88] !== isMobilePane || $[89] !== padding) {
    t37 = isMobilePane && <div className={css({
      position: "absolute",
      top: "none",
      right: "none",
      padding,
      zIndex: "[3]"
    })}>{headerActions}</div>;
    $[87] = headerActions;
    $[88] = isMobilePane;
    $[89] = padding;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== clipNav || $[92] !== t35 || $[93] !== t36 || $[94] !== t37) {
    t38 = <div className={t35}>{t36}{clipNav}{t37}</div>;
    $[91] = clipNav;
    $[92] = t35;
    $[93] = t36;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== padding) {
    t39 = css.raw({
      paddingX: padding,
      paddingBottom: "8",
      position: "relative",
      zIndex: "[2]",
      width: "fit",
      pointerEvents: "none"
    });
    $[96] = padding;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  const t40 = videoPath ? "-24px" : "0";
  let t41;
  if ($[98] !== t40) {
    t41 = {
      marginTop: t40
    };
    $[98] = t40;
    $[99] = t41;
  } else {
    t41 = $[99];
  }
  let t42;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = css({
      pointerEvents: "auto"
    });
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] !== categoryIcon || $[102] !== t42) {
    t43 = <div className={t42}>{categoryIcon}</div>;
    $[101] = categoryIcon;
    $[102] = t42;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = css({
      userSelect: "text",
      pointerEvents: "auto"
    });
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== t44 || $[106] !== title) {
    t45 = <Type variant="header-m" color="white" width="full" title={title} className={t44}>{title}</Type>;
    $[105] = t44;
    $[106] = title;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] !== t39 || $[109] !== t41 || $[110] !== t43 || $[111] !== t45) {
    t46 = <FlexColumn gap="8" className={t39} style={t41}>{t43}{t45}</FlexColumn>;
    $[108] = t39;
    $[109] = t41;
    $[110] = t43;
    $[111] = t45;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] !== description || $[114] !== isMobilePane || $[115] !== padding) {
    t47 = isMobilePane ? <ScrollableContent className={css({
      overflow: "hidden",
      display: "flex",
      flex: "1",
      minHeight: "[0]",
      touchAction: "[pan-y]"
    })} fadeRadius="12px"><FlexColumn width="full" className={css.raw({
        paddingX: padding,
        userSelect: "text"
      })}><LocationContent description={description} /></FlexColumn></ScrollableContent> : <FlexColumn width="full" className={css.raw({
      paddingX: padding,
      paddingBottom: padding,
      userSelect: "text"
    })}><LocationContent description={description} /></FlexColumn>;
    $[113] = description;
    $[114] = isMobilePane;
    $[115] = padding;
    $[116] = t47;
  } else {
    t47 = $[116];
  }
  let t48;
  if ($[117] !== t34 || $[118] !== t38 || $[119] !== t46 || $[120] !== t47) {
    t48 = <>{t34}{t38}{t46}{t47}</>;
    $[117] = t34;
    $[118] = t38;
    $[119] = t46;
    $[120] = t47;
    $[121] = t48;
  } else {
    t48 = $[121];
  }
  const content = t48;
  let t49;
  if ($[122] !== isMobilePane) {
    t49 = css.raw({
      width: isMobilePane ? "full" : "[340px]",
      background: isMobilePane ? "transparent" : "[linear-gradient(rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.075)), linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9))]",
      borderRadius: isMobilePane ? "none" : "32",
      overflow: "hidden",
      boxShadow: isMobilePane ? "[none]" : "lg",
      zIndex: "[2]",
      padding: "none",
      _compact: isMobilePane ? {
        maxWidth: "full"
      } : {
        maxWidth: "[260px]",
        borderRadius: "24"
      },
      smDown: {
        width: "full",
        maxWidth: isMobilePane ? "full" : "[calc(100vw - 32px)]",
        marginX: isMobilePane ? "0" : "12"
      }
    });
    $[122] = isMobilePane;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  const t50 = isMobilePane ? "12" : "8";
  let t51;
  if ($[124] !== isMobilePane) {
    t51 = !isMobilePane && {
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      maxHeight: MAX_HEIGHT
    };
    $[124] = isMobilePane;
    $[125] = t51;
  } else {
    t51 = $[125];
  }
  let t52;
  if ($[126] !== isMobilePane) {
    t52 = isMobilePane && {
      flex: 1,
      minHeight: 0
    };
    $[126] = isMobilePane;
    $[127] = t52;
  } else {
    t52 = $[127];
  }
  let t53;
  if ($[128] !== t51 || $[129] !== t52) {
    t53 = {
      position: "relative",
      zIndex: 4,
      ...t51,
      ...t52
    };
    $[128] = t51;
    $[129] = t52;
    $[130] = t53;
  } else {
    t53 = $[130];
  }
  let t54;
  if ($[131] !== content || $[132] !== isMobilePane) {
    t54 = isMobilePane ? content : <ScrollableContent className={css({
      flex: "1",
      minHeight: "[0]",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    })} fadeRadius="16px" scrollAreaStyle={{
      flex: "1",
      minHeight: "0",
      scrollbarWidth: "none"
    }}>{content}</ScrollableContent>;
    $[131] = content;
    $[132] = isMobilePane;
    $[133] = t54;
  } else {
    t54 = $[133];
  }
  let t55;
  if ($[134] !== t49 || $[135] !== t50 || $[136] !== t53 || $[137] !== t54) {
    t55 = <FlexColumn className={t49} gap={t50} width="full" align="stretch" style={t53} id="heatmap-event-info">{t54}</FlexColumn>;
    $[134] = t49;
    $[135] = t50;
    $[136] = t53;
    $[137] = t54;
    $[138] = t55;
  } else {
    t55 = $[138];
  }
  let t56;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = t57 => {
      const {
        open
      } = t57;
      return setModalOpen(open);
    };
    $[139] = t56;
  } else {
    t56 = $[139];
  }
  let t57;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = css({
      borderRadius: "8"
    });
    $[140] = t57;
  } else {
    t57 = $[140];
  }
  let t58;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = css({
      position: "relative"
    });
    $[141] = t58;
  } else {
    t58 = $[141];
  }
  let t59;
  if ($[142] !== title || $[143] !== videoPath) {
    t59 = videoPath ? <PinMedia videoPath={videoPath} title={title} /> : null;
    $[142] = title;
    $[143] = videoPath;
    $[144] = t59;
  } else {
    t59 = $[144];
  }
  let t60;
  if ($[145] !== clipNav || $[146] !== t58 || $[147] !== t59) {
    t60 = <div className={t58}>{t59}{clipNav}</div>;
    $[145] = clipNav;
    $[146] = t58;
    $[147] = t59;
    $[148] = t60;
  } else {
    t60 = $[148];
  }
  let t61;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = css.raw({
      padding: "8"
    });
    $[149] = t61;
  } else {
    t61 = $[149];
  }
  let t62;
  if ($[150] !== description) {
    t62 = <LocationContent description={description} />;
    $[150] = description;
    $[151] = t62;
  } else {
    t62 = $[151];
  }
  let t63;
  if ($[152] !== t61 || $[153] !== t62) {
    t63 = <FlexColumn width="full" className={t61}>{t62}</FlexColumn>;
    $[152] = t61;
    $[153] = t62;
    $[154] = t63;
  } else {
    t63 = $[154];
  }
  let t64;
  if ($[155] !== t57 || $[156] !== t60 || $[157] !== t63) {
    t64 = <ScrollableContent className={t57} hideScrollbarWhenNoOverflow={true}>{t60}{t63}</ScrollableContent>;
    $[155] = t57;
    $[156] = t60;
    $[157] = t63;
    $[158] = t64;
  } else {
    t64 = $[158];
  }
  let t65;
  if ($[159] !== modalOpen || $[160] !== t64 || $[161] !== title) {
    t65 = <Modal isOpen={modalOpen} onOpenChange={t56} size="md" title={title} disableInternalScrolling={true}>{t64}</Modal>;
    $[159] = modalOpen;
    $[160] = t64;
    $[161] = title;
    $[162] = t65;
  } else {
    t65 = $[162];
  }
  let t66;
  if ($[163] !== t55 || $[164] !== t65) {
    t66 = <>{t55}{t65}</>;
    $[163] = t55;
    $[164] = t65;
    $[165] = t66;
  } else {
    t66 = $[165];
  }
  return t66;
};
function _temp(index) {
  return index > 0 ? index - 1 : index;
}
function _temp2(layer) {
  return layer.id;
}
function _temp3(id) {
  return id.startsWith(MARKER_LAYER_PREFIX);
}
function _temp4($) {
  return $.heatmap_events.clip_title;
}
function _temp5($_0) {
  return $_0.heatmap_events.clip_description;
}
function _temp6($_1) {
  return $_1.location_info.full_screen_aria_label;
}
function _temp7($_2) {
  return $_2.location_info.close_aria_label;
}
function _temp8($_3) {
  return $_3.previous;
}
function _temp9($_4) {
  return $_4.next;
}